import React, { useState } from 'react';

/**
 * Calm duplicate-confirmation modal for Board Editor adds.
 *
 * Three flows:
 *   - SINGLE (one duplicate, no uniques): "This image is already on this board" + Add anyway / Skip.
 *   - BATCH summary (N duplicates among M items): "N of M images are already on this board" +
 *       Skip duplicates / Add all anyway / Review one by one.
 *   - BATCH review (after clicking Review): walks each duplicate item with per-item Add anyway / Skip.
 *
 * The modal NEVER blocks an upload. Uniques are passed back through the
 * resolved decision so the orchestrator can continue immediately. The user
 * always retains the option to add a duplicate intentionally (e.g. same
 * image as "light reference" and "outfit reference").
 *
 * Props
 *   open        — boolean
 *   items       — [{ key, label, thumb, existing: {existing_image_thumb,
 *                                                    existing_filename,
 *                                                    existing_section_label} }]
 *                 `items` ONLY contains the duplicates. Uniques live in the
 *                 caller's resolve payload.
 *   onResolve   — fn({ addKeys, skipKeys }) called on confirm. The orchestrator
 *                 should then upload the union of (uniques) + (addKeys) and
 *                 drop (skipKeys).
 *   onCancel    — fn() called on backdrop click or Esc — caller treats as
 *                 "skip everything"; uniques still proceed.
 *   uniqueCount — number of uniques the orchestrator is already going to add
 *                 (used in the helper sentence: "3 unique items will be added").
 */
const DuplicateConfirmModal = ({ open, items, onResolve, onCancel, uniqueCount = 0 }) => {
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [perItemDecision, setPerItemDecision] = useState({}); // key -> 'add' | 'skip'

  if (!open || !items || items.length === 0) return null;

  const isSingle = items.length === 1 && uniqueCount === 0;

  const close = () => {
    setReviewMode(false);
    setReviewIdx(0);
    setPerItemDecision({});
  };

  const handleAddAll = () => {
    onResolve({ addKeys: items.map((i) => i.key), skipKeys: [] });
    close();
  };

  const handleSkipAll = () => {
    onResolve({ addKeys: [], skipKeys: items.map((i) => i.key) });
    close();
  };

  const handleStartReview = () => {
    setReviewMode(true);
    setReviewIdx(0);
    setPerItemDecision({});
  };

  const handleReviewDecision = (decision) => {
    const item = items[reviewIdx];
    const next = { ...perItemDecision, [item.key]: decision };
    setPerItemDecision(next);
    if (reviewIdx + 1 < items.length) {
      setReviewIdx(reviewIdx + 1);
    } else {
      const addKeys = items.filter((i) => next[i.key] === 'add').map((i) => i.key);
      const skipKeys = items.filter((i) => next[i.key] === 'skip').map((i) => i.key);
      onResolve({ addKeys, skipKeys });
      close();
    }
  };

  const backdropStyle = {
    position: 'fixed', inset: 0, zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(20,18,15,0.62)',
  };
  const cardStyle = {
    background: 'var(--kahla-white)',
    padding: '36px 36px 28px',
    maxWidth: '520px',
    width: '92%',
    border: '1px solid var(--kahla-border)',
  };
  const titleStyle = {
    fontFamily: 'Playfair Display, serif',
    fontSize: '22px',
    color: 'var(--kahla-charcoal)',
    margin: '0 0 10px',
    lineHeight: 1.25,
  };
  const subtitleStyle = {
    fontFamily: '"Cormorant Garamond", serif',
    fontStyle: 'italic',
    fontSize: '15px',
    color: 'var(--kahla-text-light)',
    lineHeight: 1.5,
    margin: '0 0 22px',
  };

  // ---------- Review mode (per-item walk-through) ----------
  if (reviewMode) {
    const cur = items[reviewIdx];
    return (
      <div style={backdropStyle} onClick={onCancel} data-testid="duplicate-modal">
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          <p
            style={{
              fontSize: '10px',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              margin: '0 0 14px',
            }}
          >
            Review · {reviewIdx + 1} of {items.length}
          </p>
          <h3 style={titleStyle}>Already on this board.</h3>
          <p style={subtitleStyle}>
            You may still add it again if it represents a different idea or detail.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '18px',
              alignItems: 'center',
              padding: '16px',
              backgroundColor: 'var(--kahla-cream-light)',
              border: '1px solid var(--kahla-border)',
              marginBottom: '24px',
            }}
            data-testid={`duplicate-review-row-${cur.key}`}
          >
            {cur.existing?.existing_image_thumb && (
              <img
                src={cur.existing.existing_image_thumb}
                alt=""
                style={{
                  width: '88px',
                  height: '88px',
                  objectFit: 'cover',
                  border: '1px solid var(--kahla-border)',
                  flexShrink: 0,
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--kahla-text)',
                  margin: '0 0 4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Menlo, Consolas, monospace',
                  letterSpacing: '0.01em',
                }}
              >
                {cur.label || cur.existing?.existing_filename || '—'}
              </p>
              {cur.existing?.existing_section_label && (
                <p
                  style={{
                    fontSize: '10px',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'var(--kahla-text-light)',
                    margin: 0,
                  }}
                >
                  {cur.existing.existing_section_label}
                </p>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              className="btn-secondary"
              onClick={() => handleReviewDecision('skip')}
              data-testid="duplicate-review-skip-btn"
              style={{ fontSize: '11px', padding: '10px 22px' }}
            >
              Skip
            </button>
            <button
              className="btn-primary"
              onClick={() => handleReviewDecision('add')}
              data-testid="duplicate-review-add-btn"
              style={{ fontSize: '11px', padding: '10px 22px' }}
            >
              Add anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Single duplicate ----------
  if (isSingle) {
    const only = items[0];
    return (
      <div style={backdropStyle} onClick={onCancel} data-testid="duplicate-modal">
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          <h3 style={titleStyle}>This image is already on this board.</h3>
          <p style={subtitleStyle}>
            You may still add it again if it represents a different idea or detail.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '18px',
              alignItems: 'center',
              padding: '16px',
              backgroundColor: 'var(--kahla-cream-light)',
              border: '1px solid var(--kahla-border)',
              marginBottom: '24px',
            }}
          >
            {only.existing?.existing_image_thumb && (
              <img
                src={only.existing.existing_image_thumb}
                alt=""
                style={{
                  width: '88px',
                  height: '88px',
                  objectFit: 'cover',
                  border: '1px solid var(--kahla-border)',
                  flexShrink: 0,
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--kahla-text)',
                  margin: '0 0 4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: 'Menlo, Consolas, monospace',
                  letterSpacing: '0.01em',
                }}
              >
                {only.label || only.existing?.existing_filename || '—'}
              </p>
              {only.existing?.existing_section_label && (
                <p
                  style={{
                    fontSize: '10px',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'var(--kahla-text-light)',
                    margin: 0,
                  }}
                >
                  {only.existing.existing_section_label}
                </p>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              className="btn-secondary"
              onClick={handleSkipAll}
              data-testid="duplicate-skip-btn"
              style={{ fontSize: '11px', padding: '10px 22px' }}
            >
              Skip duplicate
            </button>
            <button
              className="btn-primary"
              onClick={handleAddAll}
              data-testid="duplicate-add-anyway-btn"
              style={{ fontSize: '11px', padding: '10px 22px' }}
            >
              Add anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Batch summary ----------
  const total = items.length + uniqueCount;
  return (
    <div style={backdropStyle} onClick={onCancel} data-testid="duplicate-modal">
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={titleStyle}>
          {items.length} of {total} {total === 1 ? 'image is' : 'images are'} already on this board.
        </h3>
        <p style={subtitleStyle}>
          {uniqueCount > 0
            ? `${uniqueCount} new ${uniqueCount === 1 ? 'item' : 'items'} will be added either way. Choose what to do with the duplicates — you may still add them if they represent a different idea or detail.`
            : 'You may still add them if they represent a different idea or detail.'}
        </p>

        {/* Compact preview row of duplicate thumbnails — read-only */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            padding: '14px',
            backgroundColor: 'var(--kahla-cream-light)',
            border: '1px solid var(--kahla-border)',
            marginBottom: '24px',
            maxHeight: '180px',
            overflowY: 'auto',
          }}
          data-testid="duplicate-summary-thumbs"
        >
          {items.slice(0, 12).map((it) => (
            <img
              key={it.key}
              src={it.existing?.existing_image_thumb || ''}
              alt=""
              title={it.label || it.existing?.existing_filename || ''}
              style={{
                width: '56px',
                height: '56px',
                objectFit: 'cover',
                border: '1px solid var(--kahla-border)',
                background: 'var(--kahla-cream)',
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ))}
          {items.length > 12 && (
            <span
              style={{
                fontSize: '10px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--kahla-text-light)',
                alignSelf: 'center',
                padding: '0 6px',
              }}
            >
              + {items.length - 12} more
            </span>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <button
            className="btn-secondary"
            onClick={handleStartReview}
            data-testid="duplicate-review-btn"
            style={{ fontSize: '11px', padding: '10px 18px' }}
          >
            Review one by one
          </button>
          <button
            className="btn-secondary"
            onClick={handleSkipAll}
            data-testid="duplicate-skip-all-btn"
            style={{ fontSize: '11px', padding: '10px 18px' }}
          >
            Skip duplicates
          </button>
          <button
            className="btn-primary"
            onClick={handleAddAll}
            data-testid="duplicate-add-all-btn"
            style={{ fontSize: '11px', padding: '10px 18px' }}
          >
            Add all anyway
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateConfirmModal;
